import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concat,
  distinctUntilChanged,
  filter,
  interval,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap
} from 'rxjs';
import { environment } from '../../environments/environment';
import { determineWorkOrderStatus } from '../helpers/helpers';
import { Status } from '../helpers/constants';

export type Marker = {
  id: number;
  lat: number;
  lon: number;
  job_status: string;
  timestamp: string;
  job_id: string;
  captain_name: string;
  servicing_aor_phone: string;
}

export type MemberLocation = {
  lat: number;
  lon: number;
}

@Injectable({
  providedIn: 'root',
})
export class MarkerService {
  baseHeaders = {
    'Content-Type': 'application/json',
    'Request-Token': environment.REQUEST_TOKEN,
  };

  captainLocationSubject = new BehaviorSubject<Marker>({
    id: 0,
    lat: 0,
    lon: 0,
    job_status: '',
    timestamp: '',
    job_id: '',
    captain_name: '',
    servicing_aor_phone: '1-800-4-seatow'
  });
  $captainLocation = this.captainLocationSubject.asObservable();

  $servicingAORPhone = this.$captainLocation.pipe(
    map(captainLocation => {
      const phone = captainLocation.servicing_aor_phone;
      return (phone && phone !== "") ? phone : '1-800-4-seatow'
    }),
    distinctUntilChanged(),
    catchError(e => {
      console.error("Error when getting servicing AOR phone", e);
      return of('1-800-4-seatow');
    })
  );

  memberLocationSubject = new BehaviorSubject<MemberLocation>({
    lat: 0,
    lon: 0,
  });
  $memberLocation = this.memberLocationSubject.asObservable();

  toastMessageSubject = new BehaviorSubject<string>('');
  $toastMessage = this.toastMessageSubject.asObservable();

  jobIdSubject = new BehaviorSubject<string>('');
  $jobId = this.jobIdSubject.asObservable();

  spinnerSubject = new BehaviorSubject<boolean>(false);
  $spinner = this.spinnerSubject.asObservable();

  jobStatusSubject = new BehaviorSubject<string>('');
  $jobStatus = this.jobStatusSubject.asObservable().pipe(
    map((currStatus) => {
      console.log('In job status pipe', currStatus);
      switch (currStatus.toLocaleLowerCase()) {
        case 'out':
          return Status.Active;
        case 'on scene':
        case 'in progress':
        case 'in service':
          return Status.Summary;
        case 'invalid':
          return Status.Invalid;
        case 'canceled':
        case 'cancelled':
        case 'declined':
          return Status.Canceled;
        case 'in':
        case 'stand down':
        case 'completed':
          return Status.Inactive
        default:
          return Status.Unavailable
      }
    }),
    distinctUntilChanged(),
    catchError((e) => {
      console.error('Error when creating currentJobStatus', e);
      return of('invalid');
    })
  );
  //-------------------------------------------------------------------

  updateIntervalSubject = new BehaviorSubject<number>(
    environment.POLL_INTERVAL || 10000
  );
  $updateInterval = this.updateIntervalSubject.asObservable();

  $captainMarker = combineLatest([
    this.$captainLocation,
    this.$updateInterval,
    this.$jobId,
  ]).pipe(
    filter(
      ([captainLocation, updateInterval, jobId]) => !!captainLocation && !!jobId
    ),
    switchMap(([captainLocation, updateInterval, jobId]) =>
      concat(
        of(captainLocation),
        interval(updateInterval).pipe(
          tap(() => {
            const jobStatus = determineWorkOrderStatus(captainLocation);
            console.log('Job Status in captain marker', jobStatus);
            this.updateJobStatus(jobStatus);
          }),
          switchMap(() =>
            this.getJobLocation(jobId).pipe(
              tap((jobLocation) => {
                console.log('JOB LOCATION in CAPTAIN MARKER', jobLocation);
                this.captainLocationSubject.next(jobLocation);
              }),
              map((newLocation) => newLocation)
            )
          )
        )
      )
    ),
    catchError((e) => {
      console.log('[CAPTAIN MARKER]: Something Went Wrong', e);
      return of(this.captainLocationSubject.getValue());
    }),
    distinctUntilChanged((prev, curr) => {
      return (
        prev?.lat == curr?.lat &&
        prev.lon == curr?.lon &&
        prev?.captain_name == curr?.captain_name &&
        prev?.timestamp == curr?.timestamp &&
        prev?.id == curr?.id
      );
    }),
    shareReplay(1)
  );

  getJobLocation(job_id: string | null): Observable<Marker> {
    const params = new HttpParams().set('service_appointment_id', job_id || '');
    return this.http
      .get(`${environment.FAST_API}v1/field_service/tyc/job_location/latest`, {
        headers: this.baseHeaders,
        observe: 'response',
        params: params,
      })
      .pipe(
        map((resp) => {
          if (resp.status != 200) {
            console.log('[GET JOB LOCATIONS]: No Job Found');
            return this.captainLocationSubject.getValue();
          }
          const nextJobLocation: any = resp.body;
          console.log(`[GET JOB LOCATIONS]: API Response`, nextJobLocation);
          const { id, lat, lon, job_status, timestamp, job_id, captain_name, servicing_aor_phone } =
            nextJobLocation[0];
          return {
            id,
            lat,
            lon,
            job_status,
            timestamp,
            job_id,
            captain_name,
            servicing_aor_phone
          };
        }),
        catchError((error) => {
          console.error('[GET JOB LOCATIONS]: Api Error', error);
          return of(this.captainLocationSubject.getValue());
        })
      );
  }

  updateCaptainLocation(value: Marker) {
    this.captainLocationSubject.next(value);
  }

  updateJobStatus(value: any) {
    this.jobStatusSubject.next(value);
  }

  constructor(private http: HttpClient) {}

  $bounds = combineLatest([this.$memberLocation, this.$captainMarker]).pipe(
    map(([member, captain]) => {
      return member.lat != 0 && member.lon != 0 ? [member, captain] : [captain];
    }),
    distinctUntilChanged((prev, curr) => {
      return JSON.stringify(prev) == JSON.stringify(curr);
    }),
    shareReplay(1)
  );

  getMemberLocation(): void {
    console.log('[GET MEMBER LOCATION]: START OF FUNCTION');
    this.spinnerSubject.next(true);
    if (!navigator.geolocation) {
      console.error('LOCATION IS NOT SUPPORTED');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.memberLocationSubject.next({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
        this.spinnerSubject.next(false);
        return;
      },
      (error) => {
        console.error(
          'Error in geolocation getcurrentposition callback',
          error
        );
        this.spinnerSubject.next(false);
        if (error?.code == 3) {
          this.showLocationError();
          return;
        }
        this.showPermissionInstructions();
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  }

  updateMemberLocationFromGeoLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => this.locationSuccess(pos),
      (pos) => this.locationError(pos),
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  locationSuccess(position: GeolocationPosition) {
    const newLocation = {
      lat: position.coords.latitude,
      lon: position.coords.longitude,
    };
    console.log('In Geolocation success callback');
    console.log('Actual Location: ', newLocation);

    this.updateMemberLocation(newLocation);
    this.updateSpinnerSubject(false);
  }

  locationError(error: GeolocationPositionError) {
    console.log('Unable to get members location', error);
    console.log('In Geolocation error callback');
    this.updateSpinnerSubject(false);
    if (error?.code == 3) {
      this.showLocationError();
      return;
    }
    this.showPermissionInstructions();
  }

  updateMemberLocation(location: MemberLocation): void {
    console.log('In update Member Markers Function');
    this.memberLocationSubject.next(location);
  }

  nextJobId(jobId: string) {
    this.jobIdSubject.next(jobId);
  }

  updateSpinnerSubject(show: boolean) {
    console.log('Next Update Spinner Value', show);
    this.spinnerSubject.next(show);
  }

  showPermissionInstructions() {
    const permissionInstructionMsg: string =
      'Please enable location permissions in your browser settings to use this feature.';
    this.toastMessageSubject.next(permissionInstructionMsg);
  }

  showLocationError() {
    const locationErrorMsg: string =
      'Sorry we were unable to find your location.';
    this.toastMessageSubject.next(locationErrorMsg);
  }
}
